import type { OntologyAgentEvent } from '@atbash-labs/ontology-agent-wire'

import { customAgentsApi } from '../api/agents/custom-agents'
import { createOntologyHeaders } from '../api/core'
import { getOntologyServiceBaseUrl } from '../config'
import type { AppDispatch, RootState } from '../store'
import {
  appendTabTurn,
  cancelTabRun,
  failTabRun,
  finishTabRun,
  setActiveTab,
  startTabRun
} from '../slices/agent-orchestrator-slice'
import {
  createAgentConversation,
  loadActiveAgentConversation,
  loadAgentConversations
} from './agent-conversations'
import { createOrchestratorEventHandler } from './orchestrator-event-router'

import { consumeSSE } from './sse-client'

export type FactoryMonitoringFamily = 'pm' | 'bottleneck' | 'yield_quality' | 'rca'

export interface RunFactoryMonitoringOptions {
  /** Optional subset of families to plan; defaults to all four on the server. */
  families?: FactoryMonitoringFamily[]
  /** Optional subset of factory line ids selected by the operator. */
  lineIds?: string[]
  /**
   * Optional per-line family selection (`{ [lineId]: FactoryMonitoringFamily[] }`).
   * When supplied, each family is only configured for the lines that selected it.
   */
  lineFamilies?: Record<string, FactoryMonitoringFamily[]>

  /** Optional per-run cap on new lines per family. */
  maxLines?: number
  /** Existing chat conversation to render the monitoring run into. */
  conversationId?: string
  /** Streamed progress notes (e.g. to drive a toast in the view layer). */
  onProgress?: (note: string) => void
  signal?: AbortSignal
}

export interface RunFactoryMonitoringResult {
  /** True when the planner finalized cleanly (no error observed). */
  completed: boolean
  /** First error message observed during the run, or null. */
  error: string | null
}

export interface FactoryMonitoringLinePreview {
  id: string
  name?: string
  site_id?: string
  equipment_count: number
  tag_count: number
}

export interface FactoryMonitoringLinesPreview {
  lines: FactoryMonitoringLinePreview[]
  lines_truncated: boolean
}

const DEFAULT_INSTRUCTION = 'Configure monitoring for my factory'

const abortControllersByConversation = new Map<string, AbortController>()

function registerAbortController(conversationId: string, controller: AbortController): void {
  const existing = abortControllersByConversation.get(conversationId)
  if (existing !== undefined && existing !== controller) existing.abort()
  abortControllersByConversation.set(conversationId, controller)
}

function clearAbortController(conversationId: string, controller: AbortController): void {
  if (abortControllersByConversation.get(conversationId) === controller) {
    abortControllersByConversation.delete(conversationId)
  }
}

export function disconnectLocalFactoryMonitoringStream(conversationId: string): void {
  const controller = abortControllersByConversation.get(conversationId)
  if (controller === undefined) return
  controller.abort()
  abortControllersByConversation.delete(conversationId)
}

export function abortFactoryMonitoringAgent(conversationId: string) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const controller = abortControllersByConversation.get(conversationId)
    const run = getState().agentOrchestrator.tabs[conversationId]?.run ?? null

    // Mirror orchestrator abort: nothing will observe the abort without a
    // local consumer, and a failed run keeps `tab.run` for its error banner.
    if (controller === undefined) {
      if (run === null) return
      if (run.streamError != null) return
      dispatch(finishTabRun({ id: conversationId }))
      return
    }

    dispatch(cancelTabRun({ id: conversationId }))
    controller.abort()
    clearAbortController(conversationId, controller)
  }
}

export async function fetchFactoryMonitoringLines(
  signal?: AbortSignal
): Promise<FactoryMonitoringLinesPreview> {
  const response = await fetch(`${getOntologyServiceBaseUrl()}/factory-monitoring/lines`, {
    headers: createOntologyHeaders(),
    signal
  })
  if (!response.ok) {
    throw new Error(`Failed to load factory lines (${response.status})`)
  }
  return (await response.json()) as FactoryMonitoringLinesPreview
}

/**
 * Drive the on-demand "Configure monitoring for my factory" run
 * inside the existing agent chat sidebar. The route emits the same
 * orchestrator-shaped event envelope as a normal chat run, while the planner
 * forwards nested custom-agent builder tool events into the matching plan step.
 *
 * UI-free by design: the view layer owns toasts/notifications (this package
 * has no UI deps). Returns a small result so the caller can report outcome.
 */
export function runFactoryMonitoringAgent(options: RunFactoryMonitoringOptions = {}) {
  return async (dispatch: AppDispatch): Promise<RunFactoryMonitoringResult> => {
    const instruction = DEFAULT_INSTRUCTION
    const conversation =
      options.conversationId !== undefined
        ? { id: options.conversationId }
        : await dispatch(createAgentConversation({ instruction }))
    const conversationId = conversation.id
    const controller = new AbortController()
    const abortFromExternalSignal = (): void => controller.abort()
    if (options.signal?.aborted) controller.abort()
    options.signal?.addEventListener('abort', abortFromExternalSignal, { once: true })
    registerAbortController(conversationId, controller)
    dispatch(setActiveTab({ id: conversationId }))
    dispatch(startTabRun({ id: conversationId, instruction, runKind: 'factory_monitoring' }))
    let observedError: string | null = null
    const router = createOrchestratorEventHandler(dispatch, conversationId)

    const body: Record<string, unknown> = {}
    if (options.families && options.families.length > 0) body.families = options.families
    if (options.lineIds !== undefined) body.line_ids = options.lineIds
    if (options.lineFamilies !== undefined) body.line_families = options.lineFamilies
    if (typeof options.maxLines === 'number') body.maxLines = options.maxLines
    body.conversation_id = conversationId
    body.instruction = instruction

    try {
      await consumeSSE<OntologyAgentEvent>({
        url: `${getOntologyServiceBaseUrl()}/factory-monitoring/run`,
        body,
        signal: controller.signal,
        onEvent: (event) => {
          router.handle(event)
          switch (event.type) {
            case 'factory_monitoring_progress':
              options.onProgress?.(event.note)
              break
            case 'error':
              if (observedError === null) observedError = event.message
              break
          }
        }
      })
    } catch (err) {
      if (controller.signal.aborted) {
        // Stopping keeps whatever the planner already produced, same as the
        // chat orchestrator: promote the partial as an aborted turn.
        if (router.didStart()) {
          dispatch(
            appendTabTurn({
              id: conversationId,
              turn: router.buildSyntheticTurn(instruction, { status: 'aborted' })
            })
          )
        }
        dispatch(finishTabRun({ id: conversationId }))
        return { completed: false, error: null }
      }
      observedError = err instanceof Error ? err.message : String(err)
    } finally {
      options.signal?.removeEventListener('abort', abortFromExternalSignal)
      clearAbortController(conversationId, controller)
    }

    // Refresh the config lists so newly-proposed paused configs appear.
    dispatch(customAgentsApi.util.invalidateTags([{ type: 'CustomAgentConfig', id: 'LIST' }]))

    const routerError = router.getObservedError()
    if (observedError === null && routerError !== null) observedError = routerError

    if (observedError) {
      dispatch(failTabRun({ id: conversationId, error: observedError }))
      return { completed: false, error: observedError }
    } else if (router.didStart()) {
      dispatch(appendTabTurn({ id: conversationId, turn: router.buildSyntheticTurn(instruction) }))
      dispatch(finishTabRun({ id: conversationId }))
      try {
        await dispatch(loadAgentConversations())
        await dispatch(loadActiveAgentConversation(conversationId))
      } catch (refetchErr) {
        console.warn('[factory-monitoring] conversation refetch failed', refetchErr)
      }
      return { completed: true, error: null }
    } else {
      const message = 'Factory monitoring ended without a final summary.'
      dispatch(failTabRun({ id: conversationId, error: message }))
      return { completed: false, error: message }
    }
  }
}
